"use client"

import * as React from "react"
import * as ToggleGroupPrimitive from "@radix-ui/react-toggle-group"
import { type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import { toggleVariants } from "@/components/ui/toggle"
import {
  segmentedCount,
  segmentedItem,
  segmentedItemActivoToggle,
  segmentedTrack,
} from "@/components/ui/segmented"

/**
 * `variant="segmented"` es el control del Design System: riel gris con el
 * activo en blanco, peso 600 y sombra baja. Es el que hay que usar para
 * cambiar de vista o filtrar dentro de una misma pantalla.
 *
 * Las variantes `default` y `outline` son las de shadcn y quedan tal cual.
 * `outline` NO sirve para esto: pinta el activo con `bg-accent` (#F4F4F5) sobre
 * un grupo de bordes unidos, y contra el lienzo el activo y el inactivo se ven
 * prácticamente iguales.
 */
type VarianteToggleGroup = VariantProps<typeof toggleVariants>["variant"] | "segmented"

type ToggleGroupVariantProps = Omit<VariantProps<typeof toggleVariants>, "variant"> & {
  variant?: VarianteToggleGroup
}

const ToggleGroupContext = React.createContext<
  ToggleGroupVariantProps & {
    spacing?: number
  }
>({
  size: "default",
  variant: "default",
  spacing: 0,
})

function ToggleGroup({
  className,
  variant,
  size,
  spacing = 0,
  children,
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Root> &
  ToggleGroupVariantProps & {
    spacing?: number
  }) {
  // El riel del segmented ya fija su propio gap y padding, así que `spacing`
  // no aplica: sólo tiene sentido para el grupo de bordes unidos.
  const esSegmented = variant === "segmented"

  return (
    <ToggleGroupPrimitive.Root
      data-slot="toggle-group"
      data-variant={variant}
      data-size={size}
      data-spacing={spacing}
      style={esSegmented ? undefined : ({ "--gap": spacing } as React.CSSProperties)}
      className={cn(
        esSegmented
          ? segmentedTrack
          : "group/toggle-group flex w-fit items-center gap-[--spacing(var(--gap))] rounded-md data-[spacing=default]:data-[variant=outline]:shadow-xs",
        className
      )}
      {...props}
    >
      <ToggleGroupContext.Provider value={{ variant, size, spacing }}>
        {children}
      </ToggleGroupContext.Provider>
    </ToggleGroupPrimitive.Root>
  )
}

function ToggleGroupItem({
  className,
  children,
  variant,
  size,
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Item> & ToggleGroupVariantProps) {
  const context = React.useContext(ToggleGroupContext)
  const varianteActiva = context.variant || variant

  return (
    <ToggleGroupPrimitive.Item
      data-slot="toggle-group-item"
      data-variant={varianteActiva}
      data-size={context.size || size}
      data-spacing={context.spacing}
      className={
        varianteActiva === "segmented"
          ? // El segmented no hereda nada de `toggleVariants`: su alto, tipografía
            // y estado activo son los del riel. Mezclarlos obligaría a pelear por
            // precedencia contra el `hover:bg-muted` y el `data-[state=on]:bg-accent`
            // de la base, que sobre el riel gris borran justamente el activo.
            cn(segmentedItem, segmentedItemActivoToggle, className)
          : cn(
              toggleVariants({
                variant: varianteActiva,
                size: context.size || size,
              }),
              "w-auto min-w-0 shrink-0 px-3 focus:z-10 focus-visible:z-10",
              "data-[spacing=0]:rounded-none data-[spacing=0]:shadow-none data-[spacing=0]:first:rounded-l-md data-[spacing=0]:last:rounded-r-md data-[spacing=0]:data-[variant=outline]:border-l-0 data-[spacing=0]:data-[variant=outline]:first:border-l",
              className
            )
      }
      {...props}
    >
      {children}
    </ToggleGroupPrimitive.Item>
  )
}

/**
 * Contador que acompaña la etiqueta de un ítem ("Pendientes 12"). Es el
 * equivalente de `<TabsCount>` para el ToggleGroup: mismo mono discreto, y
 * sólo con dato real. Sin margen propio — el ítem ya separa con `gap-1.5`.
 */
function ToggleGroupCount({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="toggle-group-count"
      className={cn(segmentedCount, className)}
      {...props}
    />
  )
}

export { ToggleGroup, ToggleGroupItem, ToggleGroupCount }
